import { CreatorInfo } from "@/components/CreatorInfo";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { RowDeleteButton } from "@/components/RowDeleteButton";
import { FlagButton, FlagBadge } from "@/components/FlagButton";
import { SaleChain } from "@/components/SaleChain";
import { StudentPickerWithCreate } from "@/components/StudentPickerWithCreate";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { fmtDate, fmtMoney, toMinor } from "@/lib/format";
import { BillingProfiles } from "@/components/BillingProfiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  submitted: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [events, setEvents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [closers, setClosers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const linkedSaleId = searchParams.get("id");
  useEffect(() => {
    if (!linkedSaleId) return;
    api.get(`/sales/${linkedSaleId}`).then(({ data }) => setSelectedSale(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, [linkedSaleId]);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, c, sk, ev, ag, of, cl] = await Promise.all([
      api.get("/sales"), api.get("/customers"),
      api.get("/catalogue/skus"), api.get("/catalogue/events"),
      api.get("/agencies"), api.get("/catalogue/offers"),
      api.get("/agencies/closers"),
    ]);
    setSales(s.data); setCustomers(c.data); setSkus(sk.data); setEvents(ev.data); setAgencies(ag.data); setOffers(of.data);
    setClosers(cl.data.filter((c) => c.active !== false));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/sales/${id}`);
    setSelectedSale(data);
  };

  return (
    <>
      <PageHeader title="Sales" subtitle="Draft, submit and confirm sales. Track outstanding balances and payment plans.">
        <Button onClick={() => setOpenCreate(true)} data-testid="new-sale-button">
          <Plus className="w-4 h-4 mr-1.5" /> New sale
        </Button>
      </PageHeader>
      <PageBody>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Number</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Allocated</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>}
              {!loading && sales.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">No sales yet. Start by adding a customer and creating a sale.</TableCell></TableRow>
              )}
              {sales.map((s) => {
                const buyer = customers.find((c) => c.id === s.buyer_id);
                return (
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => openDetail(s.id)} data-testid={`sale-row-${s.number}`}>
                    <TableCell className="font-mono text-xs">{s.number}</TableCell>
                    <TableCell>{buyer?.name || s.buyer_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_STYLE[s.status] || ""}>{s.status.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{fmtMoney(s.total_minor, s.currency)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-emerald-700">{fmtMoney(s.allocated_minor, s.currency)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{fmtMoney(s.outstanding_minor, s.currency)}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{fmtDate(s.created_at)}</TableCell>
                    <TableCell className="w-10 py-1" onClick={(e) => e.stopPropagation()}>
                      <RowDeleteButton
                        endpoint={`/sales/${s.id}`}
                        label={`sale ${s.number}`}
                        onDeleted={load}
                        testId={`sale-delete-${s.number}`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </PageBody>

      <NewSaleDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        customers={customers}
        skus={skus}
        events={events}
        agencies={agencies}
        closers={closers}
        offers={offers}
        onStudentCreated={(student) => setCustomers((prev) => [student, ...prev.filter((p) => p.id !== student.id)])}
        onCreated={() => { setOpenCreate(false); load(); }}
      />

      <Sheet open={!!selectedSale} onOpenChange={(open) => !open && setSelectedSale(null)}>
        <SheetContent className="min-w-[560px] sm:min-w-[600px] overflow-y-auto" data-testid="sale-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-mono" data-testid="sale-detail-title">
              {selectedSale?.number || "Sale details"}
              {selectedSale?.flag?.flagged && <span className="ml-2"><FlagBadge flag={selectedSale.flag} testId="sale-flag-badge" /></span>}
            </SheetTitle>
            <SheetDescription data-testid="sale-detail-description">{selectedSale ? `Buyer: ${customers.find((c) => c.id === selectedSale.buyer_id)?.name || selectedSale.buyer_id}` : "Review the Sale and linked documents."}</SheetDescription>
          </SheetHeader>
          {selectedSale && (
            <SaleDetail sale={selectedSale}
                        onChanged={async (closed) => { if (closed) { setSelectedSale(null); load(); return; } await openDetail(selectedSale.id); load(); }} />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

export function NewSaleDialog({ open, onOpenChange, customers, skus, events, agencies, closers = [], offers = [], onCreated, onStudentCreated, documentType = null, optionsLoading = false }) {
  const [buyerId, setBuyerId] = useState("");
  const [localStudents, setLocalStudents] = useState([]);
  const combinedCustomers = React.useMemo(() => {
    if (!localStudents.length) return customers;
    const seen = new Set(customers.map((c) => c.id));
    return [...localStudents.filter((s) => !seen.has(s.id)), ...customers];
  }, [customers, localStudents]);
  const [eventId, setEventId] = useState("");
  const [currency, setCurrency] = useState("MYR");
  const [agencyId, setAgencyId] = useState("");
  const [closerId, setCloserId] = useState("");
  const [notes, setNotes] = useState("");
  const newKey = () => (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
  const emptyLine = () => ({ sku_id: "", description: "", quantity: 1, unit_price: "", tax_rate: 0, _key: newKey() });
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [sstRate, setSstRate] = useState("8");
  const [pricingMode, setPricingMode] = useState("exclusive");
  const [preliminaryType, setPreliminaryType] = useState("proforma");
  const [billingId, setBillingId] = useState("personal");
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingRevision, setBillingRevision] = useState(0);
  const [saleOptions, setSaleOptions] = useState([]);
  const [existingSale, setExistingSale] = useState("new");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [plan, setPlan] = useState("full");
  const [depositAmount, setDepositAmount] = useState("");
  const [planInstalments, setPlanInstalments] = useState(3);
  const [planNotes, setPlanNotes] = useState("");
  const [terms, setTerms] = useState("30 Days");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const submitting = useRef(false);
  const command = useRef({ id: crypto.randomUUID(), payload: null });
  const kind = documentType === "proforma" ? preliminaryType : documentType || "sale";
  const title = kind === "invoice" ? "New invoice" : kind === "quotation" ? "New quotation" : kind === "proforma" ? "New pro-forma" : "New sale";
  useEffect(() => {
    setBillingId("personal"); setExistingSale("new");
  }, [buyerId]);
  useEffect(() => {
    if (!buyerId || !open) return;
    const c = new AbortController();
    Promise.all([api.get(`/students/${buyerId}/billing-profiles`, { signal: c.signal }), api.get("/sales", { params: { student_id: buyerId }, signal: c.signal })])
      .then(([billing, sales]) => { setBillingProfiles(billing.data); setSaleOptions(sales.data.filter((s) => s.status !== "cancelled")); })
      .catch((e) => { if (!c.signal.aborted) toast.error(formatApiError(e.response?.data?.detail)); });
    return () => c.abort();
  }, [buyerId, open, billingRevision]);
  const pricePayload = JSON.stringify({ pricing_mode: pricingMode, sst_rate: Number(sstRate), lines: lines.map((l) => ({
    item_code: skus.find((s) => s.id === l.sku_id)?.code || "", description: l.description,
    quantity: Number(l.quantity), unit_price_minor: toMinor(l.unit_price) })) });
  const validLines = lines.every((l) => l.sku_id && l.description.trim() && l.unit_price !== "" && Number(l.unit_price) >= 0 && Number.isInteger(Number(l.quantity)) && Number(l.quantity) > 0);
  useEffect(() => {
    setPreview(null); setPreviewError("");
    if (!open || !validLines) return;
    const c = new AbortController();
    const timer = setTimeout(() => api.post("/documents/preview", JSON.parse(pricePayload), { signal: c.signal })
      .then(({ data }) => setPreview(data)).catch((e) => { if (!c.signal.aborted) setPreviewError(formatApiError(e.response?.data?.detail)); }), 180);
    return () => { clearTimeout(timer); c.abort(); };
  }, [open, pricePayload, validLines]);

  // Recompute auto-filled prices when the event changes so operators see the
  // right price after they change the event. Manually-typed prices are kept.
  useEffect(() => {
    setLines((prev) => prev.map((l) => {
      if (!l.sku_id) return l;
      if (l.price_source === "manual") return l;
      const sku = skus.find((s) => s.id === l.sku_id);
      let source = "none";
      let priceMinor = null;
      if (eventId) {
        const evOffer = offers.find((o) => o.sku_id === l.sku_id && o.event_id === eventId
          && (o.currency || "MYR").toUpperCase() === currency.toUpperCase());
        if (evOffer) { source = "offer"; priceMinor = evOffer.price_minor; }
      }
      if (priceMinor == null && sku?.default_price_minor > 0) {
        source = "default"; priceMinor = sku.default_price_minor;
      }
      if (priceMinor == null) return l;
      return { ...l, unit_price: (priceMinor / 100).toFixed(2), price_source: source };
    }));
  }, [eventId, currency, offers, skus]);
  const reset = () => {
    setBuyerId(""); setEventId(""); setCurrency("MYR"); setAgencyId(""); setCloserId(""); setNotes(""); setFrozen(false);
    command.current = { id: crypto.randomUUID(), payload: null };
    setLines([emptyLine()]);
  };
  const chooseSale = (id) => {
    setExistingSale(id);
    if (id === "new") return;
    const sale = saleOptions.find((s) => s.id === id);
    const rate = sale.sst_rate ?? sale.lines[0]?.tax_rate ?? 0;
    setCurrency(sale.currency); setPlan(sale.payment_plan); setSstRate(String(rate));
    setDepositAmount(sale.deposit_minor ? (sale.deposit_minor / 100).toFixed(2) : "");
    setPlanInstalments(sale.plan_instalments || 3);
    setPlanNotes(sale.payment_plan_notes || sale.plan_notes || "");
    setPricingMode(sale.pricing_mode || (rate ? "exclusive" : "inclusive"));
    setAgencyId(sale.agency_id || "");
    setCloserId(sale.closer_id || "");
    setLines(sale.lines.map((l) => ({ ...l, unit_price: (l.unit_price_minor / 100).toFixed(2), _key: newKey() })));
  };
  const submit = async () => {
    if (submitting.current) return;
    if (!buyerId || !validLines || !preview || !issueDate) { toast.error("Complete the student, date and line items first"); return; }
    submitting.current = true; setSaving(true);
    try {
      if (!command.current.payload) command.current.payload = {
        request_id: command.current.id, kind, existing_sale_id: existingSale === "new" ? null : existingSale,
        billing_profile_id: billingId === "personal" ? null : billingId, issue_date: issueDate, terms,
        sst_rate: Number(sstRate), pricing_mode: pricingMode,
        sale: { buyer_id: buyerId, learner_id: buyerId, event_id: eventId || null, currency, agency_id: agencyId || null,
          closer_id: closerId || null,
          notes, payment_plan: plan,
          plan_instalments: plan === "instalment" ? Math.max(1, parseInt(planInstalments || 1)) : 1,
          deposit_minor: plan === "deposit_balance" ? Math.round(Number(depositAmount || 0) * 100) : 0,
          payment_plan_notes: planNotes || null,
          lines: lines.map((l) => ({ sku_id: l.sku_id, event_id: eventId || null,
            description: l.description, quantity: Number(l.quantity), unit_price_minor: toMinor(l.unit_price),
            line_total_minor: toMinor(l.unit_price) * Number(l.quantity), tax_minor: 0, tax_rate: Number(sstRate) })) },
      };
      setFrozen(true);
      const { data: result } = await api.post("/documents/drafts", command.current.payload);
      const collection = result.kind === "sale" ? "sales" : result.kind === "invoice" ? "invoices" : "proformas";
      const { data } = await api.get(`/${collection}/${result.id}`);
      toast.success(`${title.replace("New ", "")} drafted on one linked Sale`);
      reset(); onCreated(data);
    } catch (err) {
      try {
        await api.get(`/documents/commands/${command.current.id}`);
      } catch (check) {
        if (check.response?.status === 404) { setFrozen(false); command.current.payload = null; }
      }
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { submitting.current = false; setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!saving) onOpenChange(value); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sale-document-create-dialog" closeTestId="sale-document-close">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className={documentType ? "text-xs text-slate-500" : "sr-only"} data-testid="document-sale-link-notice">
            {documentType ? "Choose a student, billing profile and catalogue items. Reuse an existing Sale or create one linked draft Sale. Billing snapshots remain independent of the learner." : "Choose a student, billing profile, items and SST treatment for a draft Sale."}
          </DialogDescription>
        </DialogHeader>
        {optionsLoading && <p role="status" data-testid="document-options-loading">Loading customers and catalogue…</p>}
        {frozen && <div role="status" className="p-3 rounded bg-amber-50 text-amber-800 text-xs" data-testid="document-partial-creation">This save request is retained. Retry safely to recover the same Sale and document without duplicates. Keep this form open until the save completes.</div>}
        <fieldset disabled={saving || optionsLoading || frozen} className="space-y-4 disabled:opacity-60">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Student / Learner</Label>
              <StudentPickerWithCreate
                customers={combinedCustomers}
                value={buyerId}
                onChange={setBuyerId}
                onCreated={(student) => {
                  setLocalStudents((prev) => [student, ...prev.filter((p) => p.id !== student.id)]);
                  onStudentCreated?.(student);
                }}
                testId="new-sale-buyer"
              />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="sale-document-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MYR" data-testid="sale-document-currency-myr">MYR</SelectItem>
                  <SelectItem value="SGD" data-testid="sale-document-currency-sgd">SGD</SelectItem>
                  <SelectItem value="USD" data-testid="sale-document-currency-usd">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Event (optional)</Label>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger data-testid="sale-document-event"><SelectValue placeholder="Select event…" /></SelectTrigger>
                <SelectContent>
                  {events.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`sale-document-event-${e.id}`}>{e.code} · {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Agency (optional)</Label>
              <Select value={agencyId} onValueChange={setAgencyId}>
                <SelectTrigger data-testid="sale-document-agency"><SelectValue placeholder="No agency" /></SelectTrigger>
                <SelectContent>
                  {agencies.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`sale-document-agency-${a.id}`}>{a.code} · {a.name} · {a.rate_pct}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Closer (optional)</Label>
              <Select value={closerId || "__none__"} onValueChange={(v) => setCloserId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="sale-document-closer"><SelectValue placeholder="No closer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="sale-document-closer-none">No closer</SelectItem>
                  {closers.map((c) => {
                    const letters = (c.name || "").split(/\s+/)
                      .map((p) => (p.match(/[A-Za-z]/) || [""])[0])
                      .filter(Boolean).join("").slice(0, 3).toUpperCase();
                    const label = c.code || letters || "—";
                    return (
                      <SelectItem key={c.id} value={c.id} data-testid={`sale-document-closer-${c.id}`}>
                        {label} · {c.name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {buyerId && <div className="col-span-2 border rounded bg-slate-50 p-3 space-y-2"><div className="flex items-center justify-between"><Label className="text-xs">Bill To</Label><Button variant="ghost" size="sm" onClick={() => setBillingOpen(!billingOpen)} data-testid="document-manage-billing">{billingOpen ? "Hide billing manager" : "Manage billing profiles"}</Button></div><Select value={billingId} onValueChange={setBillingId}><SelectTrigger data-testid="document-billing-profile"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="personal" data-testid="document-billing-personal">Personal — use student details</SelectItem>{billingProfiles.filter((p) => p.active).map((p) => <SelectItem value={p.id} key={p.id} data-testid={`document-billing-${p.id}`}>{p.label} · {p.company || p.name}</SelectItem>)}</SelectContent></Select>{billingOpen && <BillingProfiles studentId={buyerId} onChanged={() => setBillingRevision((v) => v + 1)} />}</div>}
            {documentType && buyerId && <div className="col-span-2"><Label className="text-xs">Sale / Order link</Label><Select value={existingSale} onValueChange={chooseSale}><SelectTrigger data-testid="document-existing-sale"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new" data-testid="document-sale-new">Create one new draft Sale</SelectItem>{saleOptions.map((s) => <SelectItem value={s.id} key={s.id} data-testid={`document-sale-${s.id}`}>{s.number} · {fmtMoney(s.total_minor, s.currency)}</SelectItem>)}</SelectContent></Select>{existingSale !== "new" && <p className="text-xs text-slate-500 mt-1" data-testid="document-sale-preservation">Existing Sale remains unchanged. Document total must match its total.</p>}</div>}
            {documentType === "proforma" && <div className="col-span-2"><Label className="text-xs">Preliminary document type</Label><Select value={preliminaryType} onValueChange={setPreliminaryType}><SelectTrigger data-testid="document-preliminary-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="quotation" data-testid="document-type-quotation">Quotation — offer for approval</SelectItem><SelectItem value="proforma" data-testid="document-type-proforma">Pro-forma — preliminary payment request</SelectItem></SelectContent></Select></div>}
            <>
              <div><Label htmlFor="document-issue-date">Issue date</Label><Input id="document-issue-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="document-issue-date" /></div>
              <div><Label>Payment plan</Label><Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="document-payment-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{[["full", "Full Payment"], ["instalment", "Instalment"], ["deposit_balance", "Deposit + Balance"]].map(([value, label]) => <SelectItem key={value} value={value} data-testid={`document-plan-${value}`}>{label}</SelectItem>)}</SelectContent>
              </Select></div>
              {plan === "deposit_balance" && (
                <div className="col-span-2 rounded-md border border-amber-200 bg-amber-50 p-3 grid grid-cols-3 gap-3" data-testid="document-deposit-block">
                  <div>
                    <Label className="text-xs">Deposit amount ({currency})</Label>
                    <Input type="number" step="0.01" min="0" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="0.00" data-testid="document-deposit-amount" />
                  </div>
                  <div>
                    <Label className="text-xs">Balance</Label>
                    <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm" data-testid="document-balance">
                      {fmtMoney(Math.max(0, (preview?.total_minor || 0) - Math.round(Number(depositAmount || 0) * 100)), currency)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Total</Label>
                    <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm">
                      {fmtMoney(preview?.total_minor || 0, currency)}
                    </div>
                  </div>
                </div>
              )}
              {plan === "instalment" && (
                <div className="col-span-2 rounded-md border border-blue-200 bg-blue-50 p-3 grid grid-cols-3 gap-3" data-testid="document-instalment-block">
                  <div>
                    <Label className="text-xs">Number of instalments</Label>
                    <Input type="number" min="1" max="60" value={planInstalments} onChange={(e) => setPlanInstalments(e.target.value)} data-testid="document-instalment-count" />
                  </div>
                  <div>
                    <Label className="text-xs">Per instalment</Label>
                    <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm" data-testid="document-instalment-amount">
                      {fmtMoney(Math.floor((preview?.total_minor || 0) / Math.max(1, parseInt(planInstalments || 1))), currency)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Total</Label>
                    <div className="h-10 flex items-center px-3 rounded border border-slate-200 bg-white font-mono text-sm">
                      {fmtMoney(preview?.total_minor || 0, currency)}
                    </div>
                  </div>
                </div>
              )}
              {plan !== "full" && (
                <div className="col-span-2">
                  <Label className="text-xs">Payment arrangement notes</Label>
                  <Input value={planNotes} onChange={(e) => setPlanNotes(e.target.value)} placeholder="e.g. Balance via Maybank CC instalment 6 months" data-testid="document-plan-notes" />
                </div>
              )}
              <div><Label htmlFor="document-sst">SST rate %</Label><Input id="document-sst" type="number" min="0" max="100" step="0.01" value={sstRate} onChange={(e) => setSstRate(e.target.value)} data-testid="document-sst" /></div>
              <div><Label>Entered prices</Label><Select value={pricingMode} onValueChange={setPricingMode}><SelectTrigger data-testid="document-pricing-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="exclusive" data-testid="document-pricing-exclusive">Exclude SST — add on top</SelectItem><SelectItem value="inclusive" data-testid="document-pricing-inclusive">Include SST — extract portion</SelectItem></SelectContent></Select></div>
              {documentType && <div className="col-span-2"><Label htmlFor="document-terms">Payment / quotation terms</Label><Input id="document-terms" data-testid="document-terms" value={terms} onChange={(e) => setTerms(e.target.value)} /></div>}
            </>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line items</Label>
              <Button size="sm" variant="outline" data-testid="sale-document-add-line" onClick={() =>
                setLines([...lines, emptyLine()])
              }>+ Line</Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l._key || i} className="grid grid-cols-6 md:grid-cols-12 gap-2 items-start">
                  <div className="col-span-3 md:col-span-4">
                    <Select value={l.sku_id} onValueChange={(v) => {
                      const sku = skus.find((s) => s.id === v);
                      // Price source hierarchy (per product spec):
                      //   1. Event offer for the picked event + package + currency
                      //   2. Package default price
                      //   3. Existing line price (kept if no source)
                      let source = "none";
                      let priceMinor = null;
                      if (eventId) {
                        const evOffer = offers.find((o) => o.sku_id === v && o.event_id === eventId
                          && (o.currency || "MYR").toUpperCase() === currency.toUpperCase());
                        if (evOffer) { source = "offer"; priceMinor = evOffer.price_minor; }
                      }
                      if (priceMinor == null && sku?.default_price_minor > 0) {
                        source = "default"; priceMinor = sku.default_price_minor;
                      }
                      const autoPrice = priceMinor != null ? (priceMinor / 100).toFixed(2) : l.unit_price;
                      const copy = [...lines];
                      copy[i] = {
                        ...l,
                        sku_id: v,
                        description: l.description || sku?.default_description || sku?.name || "",
                        unit_price: autoPrice,
                        price_source: source,
                      };
                      setLines(copy);
                    }}>
                      <SelectTrigger className="text-xs" data-testid={`line-sku-${i}`}><SelectValue placeholder="Package…" /></SelectTrigger>
                      <SelectContent>
                        {skus.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`sale-document-sku-${i}-${s.id}`}>{s.code} · {s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3 md:col-span-3"><Input value={l.description} onChange={(e) => { const c = [...lines]; c[i].description = e.target.value; setLines(c); }} placeholder="Description" data-testid={`sale-document-description-${i}`} aria-label={`Description ${i + 1}`} /></div>
                  <div className="col-span-2 md:col-span-1">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      value={l.quantity}
                      data-testid={`sale-document-quantity-${i}`}
                      aria-label={`Quantity ${i + 1}`}
                      onChange={(e) => { const c = [...lines]; c[i].quantity = e.target.value; setLines(c); }}
                      className="text-right font-mono tabular-nums no-spinner"
                      placeholder="Qty"
                    />
                  </div>
                  <div className="col-span-3 md:col-span-3 flex flex-col">
                    <Input type="number" step="0.01" placeholder="0.00" value={l.unit_price} onChange={(e) => { const c = [...lines]; c[i].unit_price = e.target.value; c[i].price_source = "manual"; setLines(c); }} className="text-right font-mono tabular-nums" data-testid={`line-price-${i}`} />
                    {l.sku_id && l.price_source === "offer" && (
                      <div className="text-[10px] text-blue-700 mt-0.5 leading-tight" data-testid={`line-price-hint-${i}`}>Price from Event Offer</div>
                    )}
                    {l.sku_id && l.price_source === "default" && (
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-tight" data-testid={`line-price-hint-${i}`}>Default package price</div>
                    )}
                  </div>
                  <div className="col-span-1 md:col-span-1 flex justify-end">
                    <Button variant="ghost" size="icon" data-testid={`sale-document-remove-line-${i}`} aria-label={`Remove line ${i + 1}`} onClick={() => { if (lines.length > 1) { const c = [...lines]; c.splice(i, 1); setLines(c); } }}>
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-right text-sm">
              <span className="text-slate-500 mr-3">Total</span>
              <span className="font-mono font-semibold text-slate-900 tabular-nums" data-testid="new-sale-total">{preview ? fmtMoney(preview.total_minor, currency) : "—"}</span>
              <div className="text-xs text-slate-500 mt-1" data-testid="document-tax-total">Subtotal {preview ? fmtMoney(preview.gross_minor, currency) : "—"} · SST {sstRate}%: {preview ? fmtMoney(preview.services_tax_minor, currency) : "—"} · {pricingMode === "inclusive" ? "SST included in entered prices" : "SST added to entered prices"}</div>
              {previewError && <p className="text-xs text-rose-700" role="alert" data-testid="document-preview-error">{previewError}</p>}
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Internal notes…" data-testid="sale-document-notes" />
          </div>
        </fieldset>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)} data-testid="sale-document-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || optionsLoading || !preview} data-testid="new-sale-submit">{saving ? "Saving…" : frozen ? "Retry same save" : documentType ? "Create linked draft" : "Create draft"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaleDetail({ sale, onChanged }) {
  const [busy, setBusy] = useState(false);

  const act = async (path, extra) => {
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/${path}`, extra || null);
      toast.success(`Sale ${path}ed`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const genProforma = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/proformas", { sale_id: sale.id });
      toast.success(`Pro-forma ${data.number} created`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="space-y-5 mt-6 text-sm">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={STATUS_STYLE[sale.status]}>{sale.status.toUpperCase()}</Badge>
          <span className="text-slate-500">·</span>
          <span className="text-slate-500 text-xs">Currency {sale.currency}</span>
        </div>

        <div className="grid grid-cols-3 gap-3 text-xs">
          <div><div className="text-slate-500 uppercase tracking-wider">Total</div><div className="font-mono text-slate-900 mt-1 tabular-nums">{fmtMoney(sale.total_minor, sale.currency)}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider">Allocated</div><div className="font-mono text-emerald-700 mt-1 tabular-nums">{fmtMoney(sale.allocated_minor, sale.currency)}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider">Outstanding</div><div className="font-mono text-slate-900 mt-1 tabular-nums">{fmtMoney(sale.outstanding_minor, sale.currency)}</div></div>
        </div>

        {(sale.agency_id || sale.closer_id) && (
          <div className="grid grid-cols-2 gap-3 text-xs" data-testid="sale-detail-partners">
            <div>
              <div className="text-slate-500 uppercase tracking-wider">Agency</div>
              <div className="mt-1" data-testid="sale-detail-agency">{sale.agency_name || sale.agency_id || <span className="text-slate-400">—</span>}</div>
            </div>
            <div>
              <div className="text-slate-500 uppercase tracking-wider">Closer</div>
              <div className="mt-1" data-testid="sale-detail-closer">{sale.closer_label || sale.closer_name || <span className="text-slate-400">—</span>}</div>
            </div>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Lines</div>
          <div className="border border-slate-200 rounded overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right w-16">Qty</TableHead>
                  <TableHead className="text-right w-28">Unit</TableHead>
                  <TableHead className="text-right w-28">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sale.lines.map((l, i) => (
                  <TableRow key={`${l.sku_id || l.description || "line"}-${i}`}>
                    <TableCell className="text-xs">{l.description}</TableCell>
                    <TableCell className="text-right font-mono">{l.quantity}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(l.unit_price_minor, sale.currency)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(l.line_total_minor, sale.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {sale.proformas?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Pro-formas</div>
            <ul className="space-y-1">
              {sale.proformas.map((p) => (
                <li key={p.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs">
                  <Link to={`/proformas?id=${p.id}`} className="font-mono text-blue-700 underline" data-testid={`sale-proforma-link-${p.id}`}>{p.number}</Link>
                  <span className="text-slate-500">{p.status.toUpperCase()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!!sale.invoices?.length && <div data-testid="sale-linked-invoices">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Invoices</div>
          <ul className="space-y-1">{sale.invoices.map((invoice) => <li key={invoice.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs">
            <Link to={`/invoices?id=${invoice.id}`} className="font-mono text-blue-700 underline" data-testid={`sale-invoice-link-${invoice.id}`}>{invoice.number || "Draft invoice"}</Link>
            <span data-testid={`sale-invoice-status-${invoice.id}`}>{invoice.status.toUpperCase()}</span>
          </li>)}</ul>
        </div>}

        {sale.allocations?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Allocations</div>
            <ul className="space-y-1">
              {sale.allocations.map((a) => (
                <li key={a.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs">
                  <Link to={`/receipts?id=${a.receipt_id}`} className="font-mono text-blue-700 underline" data-testid={`sale-payment-link-${a.id}`}>{a.receipt_id.slice(0, 8)}…</Link>
                  {a.invoice_id && <Link to={`/invoices?id=${a.invoice_id}`} className="text-blue-700 underline" data-testid={`sale-allocation-invoice-${a.id}`}>Invoice</Link>}
                  <span className="font-mono text-emerald-700">{fmtMoney(a.amount_minor, a.currency)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <CreatorInfo record={sale} kind="sale" />
        <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
          {sale.status === "draft" && <Button size="sm" onClick={() => act("submit")} disabled={busy} data-testid="submit-sale">Submit</Button>}
          {(sale.status === "draft" || sale.status === "submitted") && <Button size="sm" onClick={() => act("confirm")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-sale">Confirm</Button>}
          {sale.status !== "cancelled" && <Button size="sm" variant="outline" onClick={() => act("cancel")} disabled={busy}>Cancel</Button>}
          <Button size="sm" variant="outline" onClick={genProforma} disabled={busy} data-testid="generate-proforma">Generate pro-forma</Button>
          <DeleteRecordButton
            resource="sales"
            id={sale.id}
            status={sale.status}
            label={`sale ${sale.number}`}
            testId="delete-sale-button"
            onDeleted={() => onChanged(true)}
          />
          <FlagButton resource="sales" id={sale.id} flag={sale.flag} onChanged={() => onChanged()} testId="flag-sale-button" />
        </div>
        <SaleChain saleId={sale.id} />
      </div>
    </>
  );
}
